/*
 * Integracoes externas: lista o que esta conectado e permite adicionar.
 *
 * Hoje o catalogo tem um provedor so. A tela e de lista mesmo assim porque a
 * alternativa — jogar o formulario do HubSoft direto no menu — diz que o
 * sistema so sabe falar com ele, e a proxima integracao teria de reformar a
 * tela inteira em vez de acrescentar uma linha ao catalogo.
 *
 * Para acrescentar um provedor: uma entrada em CATALOGO e o editor
 * correspondente em `editorDe`. O backend tem uma rota por provedor
 * (`/admin/integrations/<id>`), entao a rota nova entra junto.
 */
import { useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { IntegrationSettingsDto } from '../lib/types';
import { HubsoftIntegrationEditor } from '../components/HubsoftIntegrationEditor';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';

type ProvedorId = 'hubsoft';

interface Provedor {
  id: ProvedorId;
  nome: string;
  resumo: string;
  /** O que a integracao habilita, em uma linha, para a escolha ser informada. */
  paraQue: string;
}

const CATALOGO: Provedor[] = [
  {
    id: 'hubsoft',
    nome: 'HubSoft',
    resumo: 'Sistema de gestão de provedor (ERP).',
    paraQue: 'Libera o Wi-Fi para quem informa o CPF, consultando a base de clientes na hora.',
  },
];

export function IntegrationsPage() {
  const { can } = useAuth();
  const podeEscrever = can(PERMISSIONS.INTEGRATION_WRITE);

  /** Estado de cada provedor do catalogo; ausente = ainda nao adicionado. */
  const [estado, setEstado] = useState<Partial<Record<ProvedorId, IntegrationSettingsDto>>>({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState<ProvedorId | null>(null);
  const [escolhendo, setEscolhendo] = useState(false);

  useEffect(() => {
    let ativo = true;

    apiRequest<IntegrationSettingsDto>('/admin/integrations/hubsoft')
      .then((dto) => {
        if (ativo) setEstado({ hubsoft: dto });
      })
      .catch((caught: unknown) => {
        if (!ativo) return;
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, []);

  /*
   * "Adicionada" e ter configuracao gravada, nao estar ativa: o provedor entra
   * na lista assim que a credencial e salva, mesmo antes de ser ligado — e
   * justamente entre um e outro que o teste de conexao acontece.
   */
  const adicionadas = CATALOGO.filter((provedor) => estado[provedor.id]?.configurado);
  const disponiveis = CATALOGO.filter((provedor) => !estado[provedor.id]?.configurado);

  if (carregando) return <Spinner label="Carregando integrações..." />;

  if (aberta) {
    const provedor = CATALOGO.find((item) => item.id === aberta)!;

    return (
      <div className="space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{provedor.nome}</h1>
            <p className="text-sm text-slate-600">{provedor.paraQue}</p>
          </div>
          <Button variant="ghost" onClick={() => setAberta(null)}>
            Voltar
          </Button>
        </header>

        <HubsoftIntegrationEditor
          podeEscrever={podeEscrever}
          onEstado={(dto) => setEstado((atual) => ({ ...atual, hubsoft: dto }))}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Integrações</h1>
          <p className="text-sm text-slate-600">
            Sistemas externos que o hotspot consulta para decidir quem pode navegar.
          </p>
        </div>

        {podeEscrever && disponiveis.length > 0 && !escolhendo ? (
          <Button onClick={() => setEscolhendo(true)}>Adicionar integração</Button>
        ) : null}
      </header>

      {erro ? <Alert>{erro}</Alert> : null}

      {escolhendo ? (
        <Card
          title="Escolha a integração"
          action={
            <Button variant="ghost" onClick={() => setEscolhendo(false)}>
              Cancelar
            </Button>
          }
        >
          <div className="space-y-3">
            {disponiveis.map((provedor) => (
              <button
                key={provedor.id}
                type="button"
                onClick={() => {
                  setEscolhendo(false);
                  setAberta(provedor.id);
                }}
                className="block w-full rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-brand-500 hover:bg-brand-50"
              >
                <span className="block text-sm font-medium text-slate-800">{provedor.nome}</span>
                <span className="block text-xs text-slate-600">{provedor.paraQue}</span>
              </button>
            ))}

            <p className="text-xs text-slate-500">
              Por enquanto o HubSoft é a única disponível. Outros sistemas de gestão entram aqui
              conforme forem implementados.
            </p>
          </div>
        </Card>
      ) : null}

      {adicionadas.length === 0 && !escolhendo ? (
        <Card>
          <EmptyState
            title="Nenhuma integração adicionada"
            description={
              podeEscrever
                ? 'Adicione a conexão com o seu sistema de gestão para liberar o Wi-Fi por CPF.'
                : 'Seu perfil não pode configurar integrações.'
            }
            action={
              podeEscrever ? (
                <Button onClick={() => setEscolhendo(true)}>Adicionar integração</Button>
              ) : undefined
            }
          />
        </Card>
      ) : null}

      {adicionadas.map((provedor) => {
        const dto = estado[provedor.id]!;

        return (
          <Card
            key={provedor.id}
            title={provedor.nome}
            action={<Button variant="ghost" onClick={() => setAberta(provedor.id)}>Configurar</Button>}
          >
            <div className="space-y-2 text-sm">
              <p className="text-slate-600">{provedor.paraQue}</p>

              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={dto.isActive ? 'success' : 'neutral'} dot>
                  {dto.isActive ? 'ativa' : 'desligada'}
                </Badge>

                {dto.lastCheckOk === null ? null : (
                  <Badge tone={dto.lastCheckOk ? 'success' : 'danger'}>
                    {dto.lastCheckOk ? 'último teste OK' : 'último teste falhou'}
                  </Badge>
                )}

                <span className="font-mono text-xs break-all text-slate-500">{dto.baseUrl}</span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
